/**
 * Deteccao automatica dos adesivos (marcadores) na foto de perfil.
 *
 * Visao computacional classica, sem IA e sem rede: separa os pixels da cor do
 * adesivo (informada pelo avaliador ou lida tocando num adesivo da foto), junta
 * os vizinhos em manchas e fica com as que tem jeito de adesivo — redondas, de
 * tamanho parecido entre si. Roda no aparelho, numa foto ja reduzida (~1600 px).
 *
 * Quem diz QUAL marcador e cada mancha e `identificar-marcadores.ts`; aqui so se
 * acha onde ha adesivo. O avaliador sempre confere antes de salvar.
 */

/** Pixels RGBA, como `ImageData` do canvas. */
export interface Imagem {
  width: number;
  height: number;
  data: ArrayLike<number>;
}

/** Uma mancha achada: centro normalizado (0 a 1) pela largura e altura da foto. */
export interface Mancha {
  x: number;
  y: number;
  /** Area em pixels da imagem analisada. */
  area: number;
}

export interface Hsv {
  /** Matiz, 0 a 360. */
  h: number;
  /** Saturacao, 0 a 1. */
  s: number;
  /** Brilho, 0 a 1. */
  v: number;
}

/** Cores padrao do adesivo. Magenta e a recomendada (nao existe em cavalo, grama nem areia). */
export const CORES_DE_MARCADOR = [
  { hex: '#ff33cc', rotulo: 'Magenta fluorescente', aviso: null },
  { hex: '#39ff14', rotulo: 'Verde fluorescente', aviso: 'Confunde com grama: use em picadeiro de areia.' },
  { hex: '#ff7a00', rotulo: 'Laranja', aviso: 'Confunde com pelagem alazã.' },
  { hex: '#2f6bff', rotulo: 'Azul', aviso: null },
] as const;

export const COR_PADRAO_DO_MARCADOR = CORES_DE_MARCADOR[0].hex;

const HEX = /^#[0-9a-f]{6}$/;

export function corValida(hex: string | null | undefined): hex is string {
  return typeof hex === 'string' && HEX.test(hex);
}

export function rgbParaHsv(r: number, g: number, b: number): Hsv {
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === R) h = ((G - B) / d) % 6;
    else if (max === G) h = (B - R) / d + 2;
    else h = (R - G) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hexParaHsv(hex: string): Hsv {
  if (!corValida(hex)) throw new Error(`Cor inválida: ${hex}`);
  const n = Number.parseInt(hex.slice(1), 16);
  return rgbParaHsv((n >> 16) & 255, (n >> 8) & 255, n & 255);
}

export function rgbParaHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Distancia entre matizes no circulo (0 a 180). */
const distanciaDeMatiz = (a: number, b: number) => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

/**
 * Adesivo sem cor (branco, cinza): o filtro por matiz nao serve. Funciona pior
 * — parede, casco e meia branca tambem passam — e por isso pede o contraste com
 * o pelo em volta.
 */
const acromatica = (alvo: Hsv) => alvo.s < 0.25;

/** Teste de "este pixel tem a cor do adesivo", montado a partir da cor alvo. */
export function filtroDeCor(hex: string, toleranciaDeMatiz = 20): (r: number, g: number, b: number) => boolean {
  const alvo = hexParaHsv(hex);
  if (acromatica(alvo)) {
    const vMin = Math.max(0.7, alvo.v - 0.2);
    return (r, g, b) => {
      const p = rgbParaHsv(r, g, b);
      return p.s <= 0.25 && p.v >= vMin;
    };
  }
  const sMin = Math.max(0.3, alvo.s * 0.55);
  const vMin = Math.max(0.2, alvo.v * 0.45);
  return (r, g, b) => {
    const p = rgbParaHsv(r, g, b);
    return p.s >= sMin && p.v >= vMin && distanciaDeMatiz(p.h, alvo.h) <= toleranciaDeMatiz;
  };
}

export interface OpcoesDeDeteccao {
  /** Cor do adesivo, `#rrggbb`. */
  cor: string;
  toleranciaDeMatiz?: number;
  /** No maximo quantas manchas devolver (as mais parecidas com adesivo). */
  maximo?: number;
}

interface Componente {
  area: number;
  sx: number;
  sy: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** Acha as manchas com cara de adesivo. Ordem: de cima para baixo. */
export function detectarManchas(img: Imagem, opcoes: OpcoesDeDeteccao): Mancha[] {
  const { width: w, height: h, data } = img;
  const daCor = filtroDeCor(opcoes.cor, opcoes.toleranciaDeMatiz);
  const n = w * h;

  // Rotulagem em duas passadas com union-find (vizinhanca de 8).
  const rotulo = new Int32Array(n).fill(-1);
  const pai: number[] = [];
  const raiz = (a: number): number => {
    while (pai[a] !== a) {
      pai[a] = pai[pai[a]!]!;
      a = pai[a]!;
    }
    return a;
  };
  const unir = (a: number, b: number) => {
    const ra = raiz(a);
    const rb = raiz(b);
    if (ra !== rb) pai[Math.max(ra, rb)] = Math.min(ra, rb);
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const k = i * 4;
      if (!daCor(data[k]!, data[k + 1]!, data[k + 2]!)) continue;
      const vizinhos: number[] = [];
      if (x > 0 && rotulo[i - 1]! >= 0) vizinhos.push(rotulo[i - 1]!);
      if (y > 0) {
        const acima = i - w;
        if (rotulo[acima]! >= 0) vizinhos.push(rotulo[acima]!);
        if (x > 0 && rotulo[acima - 1]! >= 0) vizinhos.push(rotulo[acima - 1]!);
        if (x < w - 1 && rotulo[acima + 1]! >= 0) vizinhos.push(rotulo[acima + 1]!);
      }
      if (vizinhos.length === 0) {
        rotulo[i] = pai.length;
        pai.push(pai.length);
      } else {
        const menor = Math.min(...vizinhos);
        rotulo[i] = menor;
        for (const v of vizinhos) unir(menor, v);
      }
    }
  }

  const comps = new Map<number, Componente>();
  for (let i = 0; i < n; i++) {
    if (rotulo[i]! < 0) continue;
    const r = raiz(rotulo[i]!);
    const x = i % w;
    const y = (i - x) / w;
    const c = comps.get(r);
    if (!c) comps.set(r, { area: 1, sx: x, sy: y, x0: x, x1: x, y0: y, y1: y });
    else {
      c.area++;
      c.sx += x;
      c.sy += y;
      if (x < c.x0) c.x0 = x;
      if (x > c.x1) c.x1 = x;
      if (y < c.y0) c.y0 = y;
      if (y > c.y1) c.y1 = y;
    }
  }

  // Forma e tamanho absolutos: nem fiapo, nem parede.
  const areaMin = Math.max(6, 1e-5 * n);
  const areaMax = 3e-3 * n;
  let candidatos = [...comps.values()].filter((c) => {
    const bw = c.x1 - c.x0 + 1;
    const bh = c.y1 - c.y0 + 1;
    const preenchimento = c.area / (bw * bh); // circulo cheio ~0,785
    const alongamento = Math.max(bw, bh) / Math.min(bw, bh);
    return c.area >= areaMin && c.area <= areaMax && preenchimento >= 0.45 && alongamento <= 2.5;
  });

  // Branco: so vale mancha clara cercada de algo bem mais escuro (o pelo).
  if (acromatica(hexParaHsv(opcoes.cor))) candidatos = candidatos.filter((c) => contrastaComOEntorno(img, c));

  // Os adesivos tem todos o mesmo tamanho: fora o que destoa da mediana.
  if (candidatos.length >= 3) {
    const areas = candidatos.map((c) => c.area).sort((a, b) => a - b);
    const mediana = areas[Math.floor(areas.length / 2)]!;
    candidatos = candidatos.filter((c) => c.area >= mediana / 4 && c.area <= mediana * 4);
  }

  const maximo = opcoes.maximo ?? 40;
  if (candidatos.length > maximo) {
    const preenchimento = (c: Componente) => c.area / ((c.x1 - c.x0 + 1) * (c.y1 - c.y0 + 1));
    candidatos = candidatos.sort((a, b) => preenchimento(b) - preenchimento(a)).slice(0, maximo);
  }

  return candidatos
    .map((c) => ({ x: (c.sx / c.area + 0.5) / w, y: (c.sy / c.area + 0.5) / h, area: c.area }))
    .sort((a, b) => a.y - b.y || a.x - b.x);
}

const luminancia = (data: ArrayLike<number>, k: number) => (0.299 * data[k]! + 0.587 * data[k + 1]! + 0.114 * data[k + 2]!) / 255;

/** Anel em volta da mancha pelo menos 0,3 mais escuro (luminancia 0 a 1) que ela. */
function contrastaComOEntorno(img: Imagem, c: Componente): boolean {
  const { width: w, height: h, data } = img;
  const cx = c.sx / c.area;
  const cy = c.sy / c.area;
  const r = Math.max(c.x1 - c.x0, c.y1 - c.y0) / 2;
  let somaDentro = 0;
  let nDentro = 0;
  let somaAnel = 0;
  let nAnel = 0;
  const alcance = Math.ceil(r * 2 + 2);
  for (let y = Math.max(0, Math.floor(cy - alcance)); y <= Math.min(h - 1, Math.ceil(cy + alcance)); y++) {
    for (let x = Math.max(0, Math.floor(cx - alcance)); x <= Math.min(w - 1, Math.ceil(cx + alcance)); x++) {
      const d = Math.hypot(x - cx, y - cy);
      const l = luminancia(data, (y * w + x) * 4);
      if (d <= r * 0.6) {
        somaDentro += l;
        nDentro++;
      } else if (d >= r + 1.5 && d <= r * 2 + 2) {
        somaAnel += l;
        nAnel++;
      }
    }
  }
  if (nDentro === 0 || nAnel === 0) return false;
  return somaDentro / nDentro - somaAnel / nAnel >= 0.3;
}

/**
 * A cor do adesivo sob o dedo: dos pixels em volta do toque, a media dos mais
 * saturados (o adesivo), nao a do pelo que aparece na borda. `x`, `y` em 0-1.
 */
export function corNoPonto(img: Imagem, x: number, y: number, raio = Math.max(3, Math.round(Math.max(img.width, img.height) * 0.006))): string {
  const { width: w, height: h, data } = img;
  const cx = Math.round(x * w - 0.5);
  const cy = Math.round(y * h - 0.5);
  const amostras: { s: number; v: number; r: number; g: number; b: number }[] = [];
  for (let yy = Math.max(0, cy - raio); yy <= Math.min(h - 1, cy + raio); yy++) {
    for (let xx = Math.max(0, cx - raio); xx <= Math.min(w - 1, cx + raio); xx++) {
      if (Math.hypot(xx - cx, yy - cy) > raio) continue;
      const k = (yy * w + xx) * 4;
      const r = data[k]!;
      const g = data[k + 1]!;
      const b = data[k + 2]!;
      const { s, v } = rgbParaHsv(r, g, b);
      amostras.push({ s, v, r, g, b });
    }
  }
  if (amostras.length === 0) throw new Error('Ponto fora da foto.');
  const maxS = Math.max(...amostras.map((a) => a.s));
  // Adesivo sem cor (branco): os mais claros, em vez dos mais saturados.
  const escolhidos =
    maxS < 0.25
      ? amostras.sort((a, b) => b.v - a.v).slice(0, Math.max(1, Math.ceil(amostras.length / 4)))
      : amostras.sort((a, b) => b.s * b.v - a.s * a.v).slice(0, Math.max(1, Math.ceil(amostras.length / 4)));
  const media = (k: 'r' | 'g' | 'b') => escolhidos.reduce((t, a) => t + a[k], 0) / escolhidos.length;
  return rgbParaHex(media('r'), media('g'), media('b'));
}
